import { Controller } from "@hotwired/stimulus"

// Type-to-search dropdown for SOV Unit fields (replaces the bare <datalist>).
//
// Delegated: attach once to the SOV page wrapper —
//   data-controller="unit-select"
// and it serves every <input data-field="unit"> inside, including rows the
// build grid injects later. Free text is still allowed (no validation) —
// the list is a helper, not a constraint.
//
// UX: focus/typing opens a floating panel under the input. Empty input shows
// the full list grouped by category; typing filters across abbreviation and
// full name (code-prefix matches rank first). ↑/↓ + Enter or click selects,
// Esc closes. Selecting fills the input and blurs it so the read table's
// existing blur-persist fires exactly once.
//
// The panel is fixed-position so it can escape the tables' overflow-x-auto
// clipping, and styled inline because it's JS-built DOM.

const UNITS = [
  // Area
  { g: "Area", c: "SF", n: "Square feet" },
  { g: "Area", c: "SY", n: "Square yards" },
  { g: "Area", c: "SQ", n: "Square (100 SF, roofing)" },
  { g: "Area", c: "MSF", n: "Thousand square feet" },
  { g: "Area", c: "CSF", n: "Hundred square feet" },
  { g: "Area", c: "SFCA", n: "Square feet contact area (formwork)" },
  { g: "Area", c: "SM", n: "Square meters" },
  { g: "Area", c: "ACRE", n: "Acres" },
  // Length
  { g: "Length", c: "LF", n: "Linear feet" },
  { g: "Length", c: "IN", n: "Inches" },
  { g: "Length", c: "FT", n: "Feet" },
  { g: "Length", c: "YD", n: "Yards" },
  { g: "Length", c: "VLF", n: "Vertical linear feet" },
  { g: "Length", c: "MLF", n: "Thousand linear feet" },
  { g: "Length", c: "STA", n: "Station (100 LF)" },
  { g: "Length", c: "MI", n: "Miles" },
  { g: "Length", c: "M", n: "Meters" },
  { g: "Length", c: "MM", n: "Millimeters" },
  // Volume
  { g: "Volume", c: "CY", n: "Cubic yards" },
  { g: "Volume", c: "CF", n: "Cubic feet" },
  { g: "Volume", c: "M3", n: "Cubic meters" },
  { g: "Volume", c: "GAL", n: "Gallons" },
  { g: "Volume", c: "L", n: "Liters" },
  { g: "Volume", c: "QT", n: "Quarts" },
  { g: "Volume", c: "PT", n: "Pints" },
  { g: "Volume", c: "BBL", n: "Barrels" },
  { g: "Volume", c: "BF", n: "Board feet" },
  { g: "Volume", c: "MBF", n: "Thousand board feet" },
  { g: "Volume", c: "CCF", n: "Hundred cubic feet" },
  // Weight
  { g: "Weight", c: "LB", n: "Pounds" },
  { g: "Weight", c: "TN", n: "Tons" },
  { g: "Weight", c: "MT", n: "Metric tons" },
  { g: "Weight", c: "KG", n: "Kilograms" },
  { g: "Weight", c: "OZ", n: "Ounces" },
  { g: "Weight", c: "CWT", n: "Hundredweight (100 lb)" },
  // Count & packaging
  { g: "Count & packaging", c: "EA", n: "Each" },
  { g: "Count & packaging", c: "PR", n: "Pair" },
  { g: "Count & packaging", c: "DZ", n: "Dozen" },
  { g: "Count & packaging", c: "PC", n: "Piece" },
  { g: "Count & packaging", c: "SET", n: "Set" },
  { g: "Count & packaging", c: "KIT", n: "Kit" },
  { g: "Count & packaging", c: "LOT", n: "Lot" },
  { g: "Count & packaging", c: "PKG", n: "Package" },
  { g: "Count & packaging", c: "BX", n: "Box" },
  { g: "Count & packaging", c: "CS", n: "Case" },
  { g: "Count & packaging", c: "CTN", n: "Carton" },
  { g: "Count & packaging", c: "BAG", n: "Bag" },
  { g: "Count & packaging", c: "BDL", n: "Bundle" },
  { g: "Count & packaging", c: "PLT", n: "Pallet" },
  { g: "Count & packaging", c: "ROLL", n: "Roll" },
  { g: "Count & packaging", c: "SHT", n: "Sheet" },
  { g: "Count & packaging", c: "DR", n: "Drum" },
  { g: "Count & packaging", c: "PAIL", n: "Pail" },
  { g: "Count & packaging", c: "TUBE", n: "Tube" },
  { g: "Count & packaging", c: "CAN", n: "Can" },
  // Time & labor
  { g: "Time & labor", c: "HR", n: "Hours" },
  { g: "Time & labor", c: "DAY", n: "Days" },
  { g: "Time & labor", c: "WK", n: "Weeks" },
  { g: "Time & labor", c: "MO", n: "Months" },
  { g: "Time & labor", c: "YR", n: "Years" },
  { g: "Time & labor", c: "SHIFT", n: "Shifts" },
  { g: "Time & labor", c: "MH", n: "Man-hours" },
  { g: "Time & labor", c: "MD", n: "Man-days" },
  // Mechanical & electrical
  { g: "Mechanical & electrical", c: "CFM", n: "Cubic feet per minute" },
  { g: "Mechanical & electrical", c: "GPM", n: "Gallons per minute" },
  { g: "Mechanical & electrical", c: "KW", n: "Kilowatts" },
  { g: "Mechanical & electrical", c: "KWH", n: "Kilowatt-hours" },
  { g: "Mechanical & electrical", c: "HP", n: "Horsepower" },
  { g: "Mechanical & electrical", c: "AMP", n: "Amperes" },
  { g: "Mechanical & electrical", c: "V", n: "Volts" },
  { g: "Mechanical & electrical", c: "BTU", n: "BTUs" },
  { g: "Mechanical & electrical", c: "MBH", n: "Thousand BTU per hour" },
  { g: "Mechanical & electrical", c: "PSI", n: "Pounds per square inch" },
  // Building elements
  { g: "Building elements", c: "RSR", n: "Riser (stairs)" },
  { g: "Building elements", c: "TRD", n: "Tread (stairs)" },
  { g: "Building elements", c: "FLT", n: "Flight (stairs)" },
  { g: "Building elements", c: "OPNG", n: "Opening" },
  { g: "Building elements", c: "FIXT", n: "Fixture" },
  // Other
  { g: "Other", c: "LS", n: "Lump sum" },
  { g: "Other", c: "ALLOW", n: "Allowance" },
  { g: "Other", c: "PCT", n: "Percent" },
  { g: "Other", c: "UNIT", n: "Unit" },
  { g: "Other", c: "JOB", n: "Job" },
  { g: "Other", c: "TRIP", n: "Trip" },
  { g: "Other", c: "LOAD", n: "Load" },
  { g: "Other", c: "TEST", n: "Test" }
]

export default class extends Controller {
  connect() {
    this._input = null        // unit input currently being served
    this._options = []        // flat list of selectable option rows
    this._active = -1
    this._buildPanel()

    this._onFocusIn = (e) => { if (this._isUnitInput(e.target)) this._openFor(e.target) }
    this._onInput = (e) => { if (e.target === this._input) this._render() }
    this._onFocusOut = (e) => { if (e.target === this._input) this._close() }
    this._onKeydown = (e) => this._keydown(e)
    this._onScrollOrResize = () => this._close()

    this.element.addEventListener("focusin", this._onFocusIn)
    this.element.addEventListener("input", this._onInput)
    this.element.addEventListener("focusout", this._onFocusOut)
    this.element.addEventListener("keydown", this._onKeydown, true)
    window.addEventListener("scroll", this._onScrollOrResize, true)
    window.addEventListener("resize", this._onScrollOrResize)
  }

  disconnect() {
    this.element.removeEventListener("focusin", this._onFocusIn)
    this.element.removeEventListener("input", this._onInput)
    this.element.removeEventListener("focusout", this._onFocusOut)
    this.element.removeEventListener("keydown", this._onKeydown, true)
    window.removeEventListener("scroll", this._onScrollOrResize, true)
    window.removeEventListener("resize", this._onScrollOrResize)
    this._panel?.remove()
  }

  // ── Panel lifecycle ────────────────────────────────────────────────────────

  _isUnitInput(el) {
    return el instanceof HTMLInputElement && el.dataset.field === "unit" && !el.readOnly
  }

  _buildPanel() {
    this._panel = document.createElement("div")
    this._panel.style.cssText =
      "position:fixed;z-index:60;display:none;min-width:230px;max-width:280px;max-height:260px;" +
      "overflow-y:auto;background:#fff;border:1px solid #d1d5db;border-radius:8px;" +
      "box-shadow:0 10px 15px -3px rgba(0,0,0,0.1),0 4px 6px -4px rgba(0,0,0,0.1);" +
      "font-size:13px;padding:4px 0;"
    // mousedown (not click): runs before the input blurs, so we can keep focus
    // until the value is set, then blur once to trigger the existing persist.
    this._panel.addEventListener("mousedown", (e) => {
      const row = e.target.closest("[data-unit]")
      if (!row) { e.preventDefault(); return }
      e.preventDefault()
      this._pick(row.dataset.unit)
    })
    this._panel.addEventListener("mousemove", (e) => {
      const row = e.target.closest("[data-unit]")
      if (!row) return
      const idx = this._options.indexOf(row)
      if (idx !== -1 && idx !== this._active) { this._active = idx; this._highlight(false) }
    })
    document.body.appendChild(this._panel)
  }

  _openFor(input) {
    this._input = input
    this._render()
    const r = input.getBoundingClientRect()
    this._panel.style.left = `${Math.round(r.left)}px`
    this._panel.style.top = `${Math.round(r.bottom + 2)}px`
    this._panel.style.display = "block"
  }

  _close() {
    this._panel.style.display = "none"
    this._input = null
    this._options = []
    this._active = -1
  }

  _pick(code) {
    if (!this._input) return
    const input = this._input
    input.value = code
    // Programmatic value set fires no input event — dispatch one so the
    // grid's autosave (and anything else listening) sees the change.
    input.dispatchEvent(new Event("input", { bubbles: true }))
    this._close()
    input.blur() // read table persists on blur; build grid autosaves via input
  }

  // ── Rendering & filtering ──────────────────────────────────────────────────

  _render() {
    if (!this._input) return
    const q = this._input.value.trim().toLowerCase()
    let matches
    if (q === "") {
      matches = UNITS
    } else {
      const starts = UNITS.filter(u => u.c.toLowerCase().startsWith(q))
      const rest = UNITS.filter(u => !starts.includes(u) &&
        (u.c.toLowerCase().includes(q) || u.n.toLowerCase().includes(q)))
      matches = [...starts, ...rest]
    }

    this._panel.innerHTML = ""
    this._options = []
    this._active = -1

    if (matches.length === 0) {
      const none = document.createElement("div")
      none.style.cssText = "padding:6px 12px;color:#9ca3af;"
      none.textContent = "No match — free text is fine"
      this._panel.appendChild(none)
      return
    }

    const grouped = q === "" // group headers only when browsing the full list
    let lastGroup = null
    matches.forEach(u => {
      if (grouped && u.g !== lastGroup) {
        lastGroup = u.g
        const h = document.createElement("div")
        h.style.cssText =
          "padding:5px 12px 2px;color:#9ca3af;font-size:11px;font-weight:600;" +
          "text-transform:uppercase;letter-spacing:0.03em;"
        h.textContent = u.g
        this._panel.appendChild(h)
      }
      const row = document.createElement("div")
      row.dataset.unit = u.c
      row.style.cssText =
        "display:flex;align-items:baseline;gap:8px;padding:4px 12px;cursor:pointer;white-space:nowrap;"
      row.innerHTML =
        `<span style="font-weight:600;color:#374151;min-width:44px;">${u.c}</span>` +
        `<span style="color:#6b7280;overflow:hidden;text-overflow:ellipsis;">${u.n}</span>`
      this._panel.appendChild(row)
      this._options.push(row)
    })

    if (this._options.length > 0 && q !== "") { this._active = 0; this._highlight(false) }
  }

  _highlight(scroll = true) {
    this._options.forEach((row, i) => {
      row.style.background = i === this._active ? "#f3f4f6" : ""
    })
    if (scroll && this._active >= 0) {
      this._options[this._active].scrollIntoView({ block: "nearest" })
    }
  }

  _keydown(e) {
    if (!this._input || e.target !== this._input) return
    if (this._panel.style.display === "none") return

    if (e.key === "ArrowDown" || e.key === "ArrowUp") {
      e.preventDefault()
      if (this._options.length === 0) return
      const dir = e.key === "ArrowDown" ? 1 : -1
      this._active = (this._active + dir + this._options.length) % this._options.length
      this._highlight()
    } else if (e.key === "Enter") {
      if (this._active >= 0 && this._options[this._active]) {
        e.preventDefault()
        this._pick(this._options[this._active].dataset.unit)
      }
    } else if (e.key === "Escape") {
      e.preventDefault()
      this._close()
    }
  }
}
